import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { SCOPE_HOME, isStudioUser } from "./components/ScopedAccessGuard";
import { ReferralProvider } from "./contexts/ReferralContext";
import { ProtectedRoute, SplashScreen } from "./components";
import GlobalBottomNav from "./components/GlobalBottomNav";
import GlobalBackButton from "./components/GlobalBackButton";
import { seedAPI } from "./services/api";
import { initializeNativeApp, isNative } from "./utils/native";
import FloatingResetButton from "./components/FloatingResetButton";
import FloatingPresenceButton from "./components/FloatingPresenceButton";
import AlignmentCheck from "./components/AlignmentCheck";
import ErrorBoundary from "./components/ErrorBoundary";
import ReclaimAssistant from "./components/ReclaimAssistant";
import GlobalTeenTheme from "./components/GlobalTeenTheme";
// import BottomDockedPlayer from "./components/PrivateSuite/Premium/BottomDockedPlayer"; // REMOVED Jun 10 2026 (unwanted global chrome)
import { Toaster } from "./components/ui/sonner";
import { CreditsSheet } from "./components/voiceClone/CreditsSheet";
import { initPerfTelemetry } from "./lib/perfTelemetry";
import { LivingVisionBoard, AbundanceTileExperience, BatheExperience, BatheEngine, EnterYourReality, StateImmersion, EditYourReality, WinningMoment, PeakStateEngine, SavedImmersionsGallery, AdminImageManager, AdminVideoManager } from "./pages/Abundance";
import { PresenceJourney, MorningImmersion, MiddayInterrupt, EveningImmersion, NightAudio, PresenceActivation, PresenceLanding, PresenceActivationsPage } from "./pages/Presence";
import { BalanceHubPage, BalanceJourneyPage, BalanceDayPage, MiddayResetPage, ReleaseRitualPage } from "./pages/Balance";
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  HomePage,
  ProgramsPage,
  ProgramDetailPage,
  LessonPage,
  PracticePage,
  ProgressPage,
  AccountPage,
  UpgradePage,
  PaymentSuccessPage,
  AdminOverview,
  AdminPrograms,
  AdminProgramDetail,
  AdminUsers,
  AdminAffiliates,
  AdminImages,
  AdminChallenges,
  DailyPresencePage,
  DailyAlignmentPage,
  ChallengesPage,
  ChallengeDetailPage,
  VoiceSettingsPage,
  VoiceUploadPage,
  VoiceStudioPage,
  VoiceRecordPage,
  VoiceCloneSetupPage,
  CreateInstallationPage,
  RecordAffirmationPage,
  AffirmationLibraryPage,
  ListenPage,
  GiftCreatePage,
  GiftViewPage,
  DailyRitualPage,
  PrivateSuitePage,
  PrivateSuiteSimple,
  PrivateSuiteStatsPage,
  MyJourneysPage,
  TeenLandingPage,
  TeenChallengePage,
  TeenAffirmationsPage,
  TeenSubliminalsPage,
  TeenResetPage,
  TeenSharePage,
  TeenJourneyHistoryPage,
  TeenZonePage,
  HeroJourneyPage,
  HeroSharePage,
  ParentVerificationPage,
  ParentChallengePage,
  IntegrationJourneyPage,
  NightSessionPage,
  AbundanceJourneyPage,
  JourneyDayPage,
  AbundanceNightJourneyPage,
  AbundanceNightSessionPage,
} from "./pages";

// Networker Zone imports
import {
  NetworkerLandingPage,
  NetworkerAffirmationsPage,
  NetworkerResetsPage,
  NetworkerChallengesPage,
  NetworkerAudioLibraryPage,
  EdgeOnboarding
} from "./pages/Networker";
import {
  LivingVisionIntro,
  BankOfReclaimIntro,
  StateResetsIntro,
  PrivateSuiteIntro,
  GuidedPerformanceIntro,
} from "./pages/Networker/EdgeIntroRoutes";
import AdminExceliorTraining from "./pages/Admin/AdminExceliorTraining";
import PerformanceVisionSetup from "./pages/Networker/PerformanceVisionSetup";
import PrePerformancePriming, {
  PrePerformanceCurate,
  PrePerformanceSession,
} from "./pages/Networker/PrePerformancePriming";

// Aurum Edge — white-labelled experience for Aurum members (lazy — entire
// Aurum suite is gated behind /aurum-edge and unloaded for non-Aurum users)
const AurumEdgeLanding = lazy(() => import("./pages/AurumEdge/AurumEdgeLanding"));
const AurumWealth = lazy(() => import("./pages/AurumEdge/AurumWealth"));
const AurumVision = lazy(() => import("./pages/AurumEdge/AurumVision"));
const AurumPrivateSuite = lazy(() => import("./pages/AurumEdge/AurumPrivateSuite"));
const AurumVisionCategory = lazy(() => import("./pages/AurumEdge/AurumVisionCategory"));
const AurumVisionImmersion = lazy(() => import("./pages/AurumEdge/AurumVisionImmersion"));
const AurumAlignment = lazy(() => import("./pages/AurumEdge/AurumAlignment"));
const AurumDeepWork = lazy(() => import("./pages/AurumEdge/AurumDeepWork"));
const AurumAffirmations = lazy(() => import("./pages/AurumEdge/AurumAffirmations"));

// Inner Path — sacred / consciousness window (lazy, paid-tier gated)
const InnerPathLanding = lazy(() => import("./pages/InnerPath/InnerPathLanding"));
const InnerPathDeepWork = lazy(() => import("./pages/InnerPath/InnerPathDeepWork"));
const InnerPathDailyScript = lazy(() => import("./pages/InnerPath/InnerPathDailyScript"));
const MorningArrivalRitual = lazy(() => import("./pages/InnerPath/MorningArrivalRitual"));

// Gym — strength / training window (scaffold)
const GymLanding = lazy(() => import("./pages/Gym/GymLanding"));
const StandEasyPage = lazy(() => import("./pages/StandEasy/StandEasyPage"));
const StandEasyBreath = lazy(() => import("./pages/StandEasy/BreathSession"));
const StandEasyImmersion = lazy(() => import("./pages/StandEasy/SeImmersionPage"));
const GymDeepWork = lazy(() => import("./pages/Gym/GymDeepWork"));
const NetworkerImmersion = lazy(() => import("./pages/Networker/NetworkerImmersion"));
import PersonalLibraryPage from "./pages/PersonalLibraryPage";
import MyCollectionPage from "./pages/MyCollectionPage";
import SharedItemPage from "./pages/SharedItemPage";
import PricingPage from "./pages/PricingPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";

// Practices (Resets · State Tuning · Tapping)
import PracticesHome from "./pages/Practices/PracticesHome";
import { ResetsIntro } from "./pages/Practices/ResetsIntro";
import { StateTuningIntro, StateTuningSelect, StateTuningPlay } from "./pages/Practices/StateTuning";
import { TappingIntro, TappingSelect, TappingPlay } from "./pages/Practices/Tapping";

// Healing Support
import HealingSupportHub from "./pages/HealingSupport/HealingSupportHub";
import SpinalEase from "./pages/HealingSupport/SpinalEase";
import EmergencyNerveEase from "./pages/HealingSupport/EmergencyNerveEase";
import SpinalTrendChart from "./pages/HealingSupport/SpinalTrendChart";
import HeartFlow from "./pages/HealingSupport/HeartFlow";
import WombFlow from "./pages/HealingSupport/WombFlow";
import BrainFlow from "./pages/HealingSupport/BrainFlow";
import NervousSystemFlow from "./pages/HealingSupport/NervousSystemFlow";

// Bank of Reclaim (lazy — 1684 lines, deep-linked feature)
const BankOfReclaimPage = lazy(() => import("./pages/BankOfReclaim/BankOfReclaimPage"));

// Onboarding
import OnboardingFlow from "./pages/OnboardingFlow";

// Instant Alignment
import InstantAlignment from "./pages/InstantAlignment";

// Guided Tour
import GuidedTour from "./pages/GuidedTour";

// The Reclaim Map
// The Reclaim Map (lazy — 1300+ lines, only opened from explorer)
const ReclaimMapPage = lazy(() => import("./pages/ReclaimMapPage"));

// Performance
import GolfPerformancePage from "./pages/Performance/GolfPerformancePage";
// Reclaim Golf — dedicated Performance Operating System (lazy — premium new surface)
const GolfInterfaceLanding = lazy(() => import("./pages/Golf/GolfInterfaceLanding"));
const CollectiveAlignment = lazy(() => import("./pages/Collective/CollectiveAlignment"));
const CollectiveLibraryPage = lazy(() => import("./pages/Collective/CollectiveLibraryPage"));
// Team Alignment (Aurum Collective Alignment System — Shane's daily team rite)
const TeamAlignmentHub = lazy(() => import("./pages/TeamAlignment/TeamAlignmentHub"));
const TeamAlignmentShare = lazy(() => import("./pages/TeamAlignment/TeamAlignmentShare"));
const AdminTeamsPage = lazy(() => import("./pages/TeamAlignment/AdminTeamsPage"));
// 3-tier affiliate MLM dashboard (Reclaim's referral programme)
const AffiliateMLMDashboard = lazy(() => import("./pages/Affiliate/AffiliateMLMDashboard"));
const PartnerPackPage = lazy(() => import("./pages/PartnerPackPage"));
const AdminMLMPage = lazy(() => import("./pages/Affiliate/AdminMLMPage"));
const GolfDeepWork = lazy(() => import("./pages/Golf/GolfDeepWork"));
const GolfDeeperWorkList = lazy(() => import("./pages/Golf/GolfDeeperWorkList"));
const GolfIntroScreen = lazy(() => import("./pages/Golf/GolfIntroScreen"));
const GolfImmersion = lazy(() => import("./pages/Golf/GolfImmersion"));
const CoachPrepPage = lazy(() => import("./pages/Soulumo/CoachPrepPage"));
const SoulumoForCoaches = lazy(() => import("./pages/Soulumo/SoulumoForCoaches"));
const GolfCoachPage = lazy(() => import("./pages/Golf/GolfCoachPage"));
const GolfPhraseImmersion = lazy(() => import("./pages/Golf/GolfPhraseImmersion"));
const ReclaimRewriteEngine = lazy(() => import("./pages/Golf/ReclaimRewriteGolf"));
// Living Creation flow (lazy — heavy AI UX + image-gen UI, deep-linked only)
const PerformanceVisionFlow = lazy(() => import("./pages/LivingCreation/PerformanceVisionFlow"));
import GiftMomentCreatePage from "./pages/Gift/GiftCreatePage";
import GiftMomentViewPage from "./pages/Gift/GiftViewPage";
import HeartRuGrandma from "./pages/Public/HeartRuGrandma";
import MumSpineJourney from "./pages/Public/MumSpineJourney";
import DadHeartJourney from "./pages/Public/DadHeartJourney";
import BrandPreviewPage from "./pages/BrandPreviewPage";
import PreviewLabPage from "./pages/PreviewLabPage";
import { usePerBrandHead } from "./usePerBrandHead";

import DailyFocusPage from "./pages/Presence/DailyFocusPage";
import AdminIntegrationProgram from "./pages/Admin/AdminIntegrationProgram";
import AdminAudioStatus from "./pages/Admin/AdminAudioStatus";
import AdminFoundationsRaw from "./pages/Admin/AdminFoundationsRaw";
import FoundationsRedeem from "./pages/Soulumo/FoundationsRedeem";
import CirclesRedeemPage from "./pages/Soulumo/CirclesRedeemPage";
import AdminVoiceCoverage from "./pages/Admin/AdminVoiceCoverage";
import AdminFoundationsUpload from "./pages/Admin/AdminFoundationsUpload";
import AdminCirclesOfCare from "./pages/Admin/AdminCirclesOfCare";
import AdminVoiceBankRecorder from "./pages/Admin/AdminVoiceBankRecorder";
import VoiceLibraryPage from "./pages/Soulumo/VoiceLibraryPage";
import SignatureInstallationsPage from "./pages/Soulumo/SignatureInstallationsPage";
import SignatureCategoryPage from "./pages/Soulumo/SignatureCategoryPage";
import SignatureWorldPage from "./pages/Soulumo/SignatureWorldPage";
import VoiceClonePage from "./pages/Soulumo/VoiceClonePage";
import VoiceGiftPage from "./pages/Soulumo/VoiceGiftPage";
import SoulumoAccountPage from "./pages/Soulumo/SoulumoAccountPage";
import FoundationsHome from "./pages/Foundations/FoundationsHome";
import FoundationsPlayer from "./pages/Foundations/FoundationsPlayer";
import CareForMePage from "./pages/Foundations/CareForMePage";
import CareForSomeoneElsePage from "./pages/Foundations/CareForSomeoneElsePage";
import TogetherPage from "./pages/Together/TogetherPage";
import { ViewAsBanner } from "./components/ViewAsBanner";
import CoachGiftLanding from "./pages/Soulumo/CoachGiftLanding";
import GuideLitePage, { GuideBubble } from "./pages/Guide/GuideLitePage";
import AdminGuides from "./pages/Guide/AdminGuides";
import AdminCosts from "./pages/Admin/AdminCosts";
import AdminLibraryGrowth from "./pages/Admin/AdminLibraryGrowth";
import AdminAffiliateTax from "./pages/Admin/AdminAffiliates_Tax";
import AffiliateDetailsPage from "./pages/Soulumo/AffiliateDetailsPage";
import PartnerAreaPage from "./pages/Soulumo/PartnerAreaPage";
import AdminCommissions from "./pages/Admin/AdminCommissions";
import MembershipPage from "./pages/Soulumo/MembershipPage";
import { AreaGate } from "./components/membership/AreaGate";
import AdminTalkingClips from "./pages/Admin/AdminTalkingClips";
import OneMillionLightsPage from "./pages/OneMillionLights/OneMillionLightsPage";
import SoulumoLanding from "./pages/Soulumo/SoulumoLanding";
import EcosystemHome from "./pages/Ecosystem/EcosystemHome";
import WhySoulumoPage from "./pages/Soulumo/WhySoulumoPage";
import SoulumoAuthPage from "./pages/Soulumo/SoulumoAuthPage";
import MyLibraryPage from "./pages/Soulumo/MyLibraryPage";
import ReclaimGolfHub from "./pages/Golf/ReclaimGolfHub";
// Mar 8 2026 · Golf hub is a Reclaim upsell product, not Soulumo.
// Soulumo stays flat: catalogue of audio installations only.
import WellbeingPage from "./pages/Soulumo/WellbeingPage";
import MusicPage from "./pages/Soulumo/MusicPage";
import { TermsPage, PrivacyPage } from "./pages/Soulumo/LegalPages";
import { resolveBrand, brandHomePath, isBrandedNarrow } from "./lib/brand";
import PrivacyPromisePage from "./pages/Soulumo/PrivacyPromisePage";
import AdminAudioResync from "./pages/Admin/AdminAudioResync";
import AdminGiftPriming from "./pages/Admin/AdminGiftPriming";
import BiohackingSleep from "./pages/Biohacking/BiohackingSleep";
import FoundationsSleepPage from "./pages/Foundations/FoundationsSleepPage";
import DailyEssentialsPage from "./pages/DailyEssentials/DailyEssentialsPage";
import MorningActivationShare from "./pages/Share/MorningActivationShare";
import PublicShareAudio from "./pages/Share/PublicShareAudio";
import AdminShareLinks from "./pages/Admin/AdminShareLinks";
import AdminFamilyKeepsakes from "./pages/Admin/AdminFamilyKeepsakes";
import AdminGolfScripts from "./pages/Admin/AdminGolfScripts";
import AdminGolfSignatures from "./pages/Admin/AdminGolfSignatures";
import AdminConcierge from "./pages/Admin/AdminConcierge";
import AdminBulkInvite from "./pages/Admin/AdminBulkInvite";
import AdminBetaInvites from "./pages/Admin/AdminBetaInvites";
import AdminInfluencers from "./pages/Admin/AdminInfluencers";
import AdminGolfPartners from "./pages/Admin/AdminGolfPartners";
import { InfluencerEntry, InfluencerStats } from "./pages/Ecosystem/Influencer";
import "./lib/via";
import AdminStuckResets from "./pages/Admin/AdminStuckResets";
import AdminGrantAccess from "./pages/Admin/AdminGrantAccess";
import AdminMergeAccounts from "./pages/Admin/AdminMergeAccounts";
import AdminLibraryRestore from "./pages/Admin/AdminLibraryRestore";
import SoulumoDemoPage from "./pages/Soulumo/SoulumoDemoPage";
import SoulumoLaunchPage from "./pages/Soulumo/SoulumoLaunchPage";
import SoulumoHeroOptions from "./pages/Soulumo/SoulumoHeroOptions";
import TogetherSetupPage from "./pages/Soulumo/TogetherSetupPage";
import SoulumoTabBar from "./components/soulumoNav/SoulumoTabBar";
import { StandEasySkin, standEasyActive } from "./components/StandEasySkin";
import AdminPayments from "./pages/Admin/AdminPayments";
import AdminVoiceCloneInterest from "./pages/Admin/AdminVoiceCloneInterest";
import AdminFoundingLightReservations from "./pages/Admin/AdminFoundingLightReservations";
import AdminBetaFeedback from "./pages/Admin/AdminBetaFeedback";
import AdminBetaIntelligence from "./pages/Admin/AdminBetaIntelligence";
import AdminJourneyDiagnostics from "./pages/Admin/AdminJourneyDiagnostics";
import AdminExports from "./pages/Admin/AdminExports";
import MagicLoginPage from "./pages/MagicLoginPage";
import JoinReferralPage from "./pages/JoinReferralPage";
import AdminJourneyImages from "./pages/Admin/AdminJourneyImages";
import AdminNetworkerVisionImages from "./pages/Admin/AdminNetworkerVisionImages";
import IntegrationProgramPage from "./pages/IntegrationProgram/IntegrationProgramPage";
import ComingSoon from "./pages/ComingSoon";
// Manifestations (lazy — heavy gallery, deep-linked from Reclaim Map)
const ManifestationsPage = lazy(() => import("./pages/Manifestations/ManifestationsPage"));
import FounderInterviewPage from "./pages/FounderInterview";
// Excelior chat (lazy — large chat history component)
const ExceliorChat = lazy(() => import("./pages/ExceliorChat"));
import PreviewModeIndicator from "./components/PreviewModeIndicator";

// Reclaim Absorption Engine — top-level "Journeys" area
import JourneysHub from "./pages/Journeys/JourneysHub";
import AbsorptionDayPage from "./pages/Journeys/JourneyDayPage";
const PersonalJourneyCreate = lazy(() => import("./pages/Journeys/PersonalJourneyCreate"));
const PersonalJourneyDayPage = lazy(() => import("./pages/Journeys/PersonalJourneyDayPage"));
import JourneyDetailPage from "./pages/Journeys/JourneyDetailPage";
import MockTestPage from "./pages/Journeys/MockTestPage";

// Your Living Dream — Phase 1a cinematic surface (lazy — three small,
// composable pages chained together; never loaded unless visited).
const LivingDreamArrival = lazy(() => import("./pages/LivingDream/LivingDreamArrival"));
const LivingDreamWrite   = lazy(() => import("./pages/LivingDream/LivingDreamWrite"));
const LivingDreamResult  = lazy(() => import("./pages/LivingDream/LivingDreamResult"));
const LivingDreamLibrary = lazy(() => import("./pages/LivingDream/LivingDreamLibrary"));
const LivingDreamBoard   = lazy(() => import("./pages/LivingDream/LivingDreamBoard"));

// The Reclaim Atelier — ultra-premium tier of Your Living Dream
const AtelierHub        = lazy(() => import("./pages/Atelier/AtelierHub"));
const AtelierWrite      = lazy(() => import("./pages/Atelier/AtelierWrite"));
const AtelierCurating   = lazy(() => import("./pages/Atelier/AtelierCurating"));
const AtelierDashboard  = lazy(() => import("./pages/Atelier/AtelierDashboard"));
const AtelierGiftReveal = lazy(() => import("./pages/Atelier/AtelierGiftReveal"));
const SharedDream = lazy(() => import("./pages/Atelier/SharedDream"));
const GolfPartnerPage = lazy(() => import("./pages/GolfPartners/GolfPartnerPage"));
const MyDream           = lazy(() => import("./pages/Atelier/MyDream"));
const KingsRitualHub    = lazy(() => import("./pages/Atelier/KingsRitual/KingsRitualHub"));
const KingsRitualDay    = lazy(() => import("./pages/Atelier/KingsRitual/KingsRitualDay"));
const AtelierIdentityRitual = lazy(() => import("./pages/Atelier/KingsRitual/AtelierIdentityRitual"));
const FootballHome     = lazy(() => import("./pages/Football/FootballHome"));
const AbsorptionEngine = lazy(() => import("./pages/Absorption/AbsorptionEngine"));

// Rewrite Engine Hub — choose-your-domain landing for all 6 rituals.
const RewriteEngineHub = lazy(() => import("./pages/RewriteEngineHub"));

import "./App.css";

// ── BrandRoot · Mar 3 2026 ──────────────────────────────────────
// Renders the right front door for the current hostname:
//   • soulumo.app          → <SoulumoLanding /> (public)
//   • onemillionlights.org → <OneMillionLightsPage /> (public)
//   • reclaim.app / other  → the classic Reclaim <HomePage /> (protected)
function BrandRoot() {
  const brand = resolveBrand();
  if (brand === "soulumo") return <SoulumoLanding />;
  if (brand === "oml")     return <OneMillionLightsPage />;
  if (isStudioHost())      return <AdminReclaimHome />;
  return <ReclaimRoot />;
}

// Journey-restricted users keep landing on their journey; everyone else gets the ecosystem home.
function ReclaimRoot() {
  const { user } = useAuth();
  const slug = user?.role !== "admin" && user?.restricted_journeys?.[0];
  if (slug) return <Navigate to={`/journeys/${slug}`} replace />;
  return <EcosystemHome />;
}

// The classic Reclaim app home is still in development: admins and studio testers only.
// studio.<domain> is the private working entrance and opens straight here.
const isStudioHost = () => (window.location.host || "").toLowerCase().startsWith("studio.");
function AdminReclaimHome() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen" />;
  if (!user) return <Navigate to="/login?redirect=/reclaim-home" replace />;
  if (user.role === "admin" || user.access_scope === "studio_all") return <ProtectedRoute><HomePage /></ProtectedRoute>;
  if (SCOPE_HOME[user.access_scope]) return <Navigate to={SCOPE_HOME[user.access_scope]} replace />;
  if (isStudioHost()) return <StudioPrivate />;
  return <Navigate to="/" replace />;
}

const StudioPrivate = () => (
  <div className="min-h-screen flex items-center justify-center px-6 text-center" style={{ background: "#070D0B", color: "#F3EDDD" }} data-testid="studio-private">
    <div>
      <p className="text-[0.7rem] tracking-[0.55em]" style={{ color: "#D7BB7A" }}>RECLAIM STUDIO</p>
      <p className="mt-4 text-3xl font-light" style={{ fontFamily: '"Cormorant Garamond", serif' }}>This is our private working space.</p>
      <a href="https://reclaim.app" className="mt-6 inline-block text-sm underline underline-offset-4" style={{ color: "#D7BB7A" }} data-testid="studio-private-home-link">Visit Reclaim</a>
    </div>
  </div>
);

/**
 * Feb 22 2026 · Foundations premium-gift review.
 *
 * Wraps chrome (BottomNav, FABs, Assistant) and suppresses everything
 * on either a narrow-brand host (Soulumo / OML) or on the immersive
 * `/foundations/*` gift routes. Foundations is a curated gift
 * experience — Michael's review: "keep floating icons from competing
 * with the main action" — so we hide them entirely there.
 */
function NarrowChromeGate({ narrowBrand, children }) {
  const location = useLocation();
  const { user } = useAuth();
  const path = location.pathname || "";
  // Oct 2026 · studio lock: the Reclaim shell (bottom nav, FABs) only shows for people allowed into the studio.
  if (!isStudioUser(user) || user?.access_scope === "stand_easy_tester" || standEasyActive(path, user)) return null;
  // Mar 2026 · Michael — Soulumo, Foundations, OML and the Together
  // invitation page must always feel like standalone products, never
  // "a section of Reclaim". Suppress all Reclaim chrome (back button,
  // bottom nav, Reset FAB, Presence FAB, Assistant) on these paths
  // regardless of the host we're served from, so previews on the
  // Reclaim domain match the production narrow-brand hosts exactly.
  //
  // Oct 3 2026 · Added /biohacking/sleep so the Sleep Biohacking
  // area can be surfaced inside Foundations ("I need to rest" →
  // Sleep Biohacking) without the Reclaim shell wrapping it. The
  // visual language is still Reclaim's dark-blue palette for now;
  // we'll port it to Foundations navy + gold in a follow-up.
  const onFoundations = path.startsWith("/foundations");
  const onTogether = path.startsWith("/together");
  const onSoulumo = path === "/soulumo" || path.startsWith("/soulumo/") ||
    ["/my-installations", "/my-journeys", "/my-library", "/signature-installations", "/voice-clone", "/voice-gift", "/voice-library"]
      .some((p) => path === p || path.startsWith(`${p}/`));
  const onOml =
    path === "/one-million-lights" ||
    path.startsWith("/one-million-lights/") ||
    path === "/lights" ||
    path.startsWith("/lights/");
  const onBiohackingSleep = path.startsWith("/biohacking/sleep") || path.startsWith("/stand-easy");
  if (narrowBrand || (path === "/" && !isStudioHost()) || path.startsWith("/with") || path === "/reclaim" || path.startsWith("/partners") || path.startsWith("/dream/shared") || onFoundations || onTogether || onSoulumo || onOml || onBiohackingSleep) return null;
  return <>{children}</>;
}

function App() {
  // Feb 27 2026 · Ring-fence brand assets — swap apple-touch-icon,
  // manifest, favicon and page title based on the hostname so
  // soulumo.app users get the golden orb, onemillionlights.org
  // users get the amber light, reclaim.app users keep the leaf.
  usePerBrandHead();
  // Mar 5 2026 · When the current hostname (or ?brand=) is a narrowed
  // brand — Soulumo or One Million Lights — we hide all the Reclaim
  // chrome (Reset FAB, Presence FAB, Assistant, back button, bottom nav)
  // so the landing feels like its own standalone product.
  const narrowBrand = isBrandedNarrow(resolveBrand());

  // Feb 22 2026 · Michael's Foundations premium-gift review. The
  // Foundations home is a curated gift experience — none of the
  // Reclaim floating chrome should surface here, even when the user
  // is on the main brand host. We check `location.pathname` inside
  // BrowserRouter (below) via NarrowChromeGate.

  // DEMO MODE — splashes/onboarding/alignment/ritual all suppressed so the
  // app can be shown to people without interruptions. Re-enable when ready.
  const [showSplash, setShowSplash] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [showAlignmentSignal, setShowAlignmentSignal] = useState(false);
  const [showDailyRitual, setShowDailyRitual] = useState(false);
  const [ritualChecked, setRitualChecked] = useState(true);
  const [alignmentChecked, setAlignmentChecked] = useState(true);

  useEffect(() => {
    // Initialize native app features (Capacitor)
    initializeNativeApp();

    // Performance telemetry — captures real user web vitals so we can see
    // which pages feel slow in the wild and target fixes at evidence.
    initPerfTelemetry();

    // Run seed and mark app as ready
    seedAPI.run()
      .catch(() => {})
      .finally(() => setAppReady(true));
  }, []);

  // Check if we should show alignment signal after splash
  useEffect(() => {
    if (!showSplash && !alignmentChecked) {
      checkAlignmentSignal();
    }
  }, [showSplash, alignmentChecked]);

  // Check if we should show daily ritual after alignment signal closes
  useEffect(() => {
    if (!showSplash && alignmentChecked && !showAlignmentSignal && !ritualChecked) {
      // If alignment was just completed today, skip the ritual (avoid duplicate "how are you feeling")
      const today = new Date().toDateString();
      const alignmentJustCompleted = localStorage.getItem("alignment_check_date") === today;
      
      if (alignmentJustCompleted) {
        // User already did alignment check - skip ritual
        setRitualChecked(true);
        return;
      }
      
      checkDailyRitual();
    }
  }, [showSplash, alignmentChecked, showAlignmentSignal, ritualChecked]);

  // Also listen for storage changes (login events)
  useEffect(() => {
    const handleStorageChange = () => {
      const token = localStorage.getItem("reclaim_token");
      if (token && !showSplash && !alignmentChecked) {
        checkAlignmentSignal();
      }
    };

    // Check periodically for login (since storage events don't fire in same tab)
    const interval = setInterval(() => {
      const token = localStorage.getItem("reclaim_token");
      if (token && !showSplash && !alignmentChecked) {
        checkAlignmentSignal();
      }
    }, 500);

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [showSplash, alignmentChecked]);

  // Check if we should show alignment signal
  const checkAlignmentSignal = () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) return;

    // Don't interrupt deep flows — daily alignment signal must NOT
    // pop over the Personal Journey wizard, the immersive Private
    // Suite session, or the Team Alignment journey, otherwise a
    // mis-tap steals focus and navigates the user away from
    // whatever they're building. Jun 9 2026.
    const p = (typeof window !== "undefined" && window.location?.pathname) || "";
    const blockedPaths = ["/my-journeys", "/private-suite", "/team-alignment", "/immersion"];
    if (blockedPaths.some((bp) => p.startsWith(bp))) {
      setAlignmentChecked(true);
      return;
    }

    setAlignmentChecked(true);

    const today = new Date().toDateString();
    const lastAlignment = localStorage.getItem("reclaim_last_alignment");

    // Show alignment signal if not done today
    if (lastAlignment !== today) {
      setShowAlignmentSignal(true);
    }
  };

  const checkDailyRitual = async () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) return; // Not logged in

    // Same deep-flow guard as the alignment signal — don't interrupt
    // the journey builder, private suite session, team alignment,
    // or immersion. Jun 9 2026.
    const p = (typeof window !== "undefined" && window.location?.pathname) || "";
    const blockedPaths = ["/my-journeys", "/private-suite", "/team-alignment", "/immersion"];
    if (blockedPaths.some((bp) => p.startsWith(bp))) {
      setRitualChecked(true);
      return;
    }

    // Mark as checked so we don't keep polling
    setRitualChecked(true);
    
    // Always check with backend first (source of truth)
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/daily-ritual/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.completed_today) {
        // Backend says completed - sync localStorage and don't show
        localStorage.setItem("reclaim_ritual_completed", new Date().toDateString());
        return;
      }
      
      // Backend says not completed - check if skipped locally today
      const today = new Date().toDateString();
      const skippedToday = localStorage.getItem("reclaim_ritual_skipped") === today;
      
      if (!skippedToday) {
        setShowDailyRitual(true);
      }
    } catch (err) {
      console.log("Could not check ritual status:", err);
      // On error, fall back to localStorage check
      const today = new Date().toDateString();
      const skippedToday = localStorage.getItem("reclaim_ritual_skipped") === today;
      const completedToday = localStorage.getItem("reclaim_ritual_completed") === today;
      
      if (!skippedToday && !completedToday) {
        setShowDailyRitual(true);
      }
    }
  };

  const handleRitualComplete = () => {
    localStorage.setItem("reclaim_ritual_completed", new Date().toDateString());
    setShowDailyRitual(false);
  };

  const handleRitualSkip = () => {
    localStorage.setItem("reclaim_ritual_skipped", new Date().toDateString());
    setShowDailyRitual(false);
  };

  const handleAlignmentClose = () => {
    // Mark as closed for today so it doesn't reappear
    localStorage.setItem("reclaim_last_alignment", new Date().toDateString());
    setShowAlignmentSignal(false);
  };

  const handleSplashComplete = () => {
    const finishSplash = () => {
      setShowSplash(false);
      // Check if onboarding has been seen
      const seen = localStorage.getItem('reclaim_onboarding_seen');
      if (!seen) {
        setShowOnboarding(true);
      }
    };
    if (appReady) {
      finishSplash();
    } else {
      const checkReady = setInterval(() => {
        if (appReady) {
          finishSplash();
          clearInterval(checkReady);
        }
      }, 100);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  return (
    <ErrorBoundary>
      <ReferralProvider>
        <AuthProvider>
          <GlobalTeenTheme>
          {showSplash && (
            <SplashScreen onComplete={handleSplashComplete} minDuration={2500} />
          )}
          
          {showOnboarding && (
            <OnboardingFlow onComplete={handleOnboardingComplete} />
          )}
          
          <BrowserRouter>
            <ErrorBoundary>
              <Suspense fallback={
                <div
                  className="min-h-screen flex items-center justify-center"
                  style={{ background: 'radial-gradient(ellipse at center, #0F141C 0%, #02040B 75%)' }}
                  data-testid="lazy-route-loading"
                >
                  <div
                    className="w-10 h-10 rounded-full border-2 border-white/15 border-t-white/55 animate-spin"
                    aria-label="Loading"
                  />
                </div>
              }>
              <Routes>
            {/* Public Routes */}
            <Route path="/listen/:installationId" element={<ListenPage />} />
            <Route path="/brand/solumo" element={<BrandPreviewPage />} />
            <Route path="/brand/soulumo" element={<BrandPreviewPage />} />
            <Route path="/brand-preview" element={<BrandPreviewPage />} />
            <Route path="/foundations" element={<FoundationsHome />} />
            <Route path="/foundations/care-for-me" element={<CareForMePage />} />
            <Route path="/foundations/care-for-someone-else" element={<CareForSomeoneElsePage />} />
            <Route path="/foundations/play/:itemId" element={<FoundationsPlayer />} />
            <Route path="/together" element={<TogetherPage />} />
            <Route path="/one-million-lights" element={<OneMillionLightsPage />} />
            <Route path="/lights" element={<OneMillionLightsPage />} />
            <Route path="/preview-lab" element={<PreviewLabPage />} />
            <Route path="/gift/:giftId" element={<GiftViewPage />} />
            <Route path="/heart-ru/grandma" element={<HeartRuGrandma />} />
            <Route path="/spine/mum" element={<MumSpineJourney />} />
            <Route path="/heart/dad" element={<DadHeartJourney />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* Payment Routes */}
            <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />

            {/* Protected Routes */}
            {/* Mar 3 2026 · hostname-based front door: on soulumo.app
                and onemillionlights.org, `/` redirects to the branded
                landing so each domain feels stand-alone. Reclaim's
                default HomePage still serves for reclaim.app. */}
            <Route path="/" element={<BrandRoot />} />
            <Route path="/reclaim-home" element={<AdminReclaimHome />} />
            <Route path="/reclaim" element={<EcosystemHome />} />
            <Route path="/soulumo" element={<SoulumoLanding />} />
            <Route path="/soulumo/why" element={<WhySoulumoPage />} />
            <Route path="/soulumo/join" element={<SoulumoAuthPage />} />
            <Route path="/my-library" element={<ProtectedRoute><MyLibraryPage /></ProtectedRoute>} />
            <Route path="/library" element={<ProtectedRoute><MyLibraryPage /></ProtectedRoute>} />
            <Route path="/soulumo/golf" element={<ReclaimGolfHub />} />
            <Route path="/reclaim/golf" element={<ReclaimGolfHub />} />
            <Route path="/soulumo/wellbeing" element={<WellbeingPage />} />
            <Route path="/soulumo/music" element={<MusicPage />} />
            <Route path="/soulumo/terms" element={<TermsPage />} />
            <Route path="/soulumo/privacy" element={<PrivacyPage />} />
            <Route path="/coming-soon" element={<ProtectedRoute><ComingSoon /></ProtectedRoute>} />
            <Route path="/abundance/manifestations" element={<ProtectedRoute><ManifestationsPage /></ProtectedRoute>} />
            <Route path="/admin/founder-interview" element={<ProtectedRoute><FounderInterviewPage /></ProtectedRoute>} />
            <Route path="/excelior" element={<ProtectedRoute><ExceliorChat /></ProtectedRoute>} />
            <Route path="/ask-michael" element={<ProtectedRoute><ExceliorChat /></ProtectedRoute>} />
            <Route path="/map" element={<ProtectedRoute><ReclaimMapPage /></ProtectedRoute>} />
            <Route path="/programs" element={<ProtectedRoute><ProgramsPage /></ProtectedRoute>} />
            <Route path="/programs/:programId" element={<ProtectedRoute><ProgramDetailPage /></ProtectedRoute>} />
            <Route path="/lesson/:lessonId" element={<ProtectedRoute><LessonPage /></ProtectedRoute>} />
            <Route path="/practice" element={<ProtectedRoute><PracticePage /></ProtectedRoute>} />
            <Route path="/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />
            <Route path="/soulumo/account" element={<ProtectedRoute><SoulumoAccountPage /></ProtectedRoute>} />
            <Route path="/account/voice-settings" element={<ProtectedRoute><VoiceSettingsPage /></ProtectedRoute>} />
            <Route path="/account/voice-upload" element={<ProtectedRoute><VoiceUploadPage /></ProtectedRoute>} />
            <Route path="/voice-upload" element={<ProtectedRoute><VoiceUploadPage /></ProtectedRoute>} />
            <Route path="/presence" element={<ProtectedRoute><DailyPresencePage /></ProtectedRoute>} />
            <Route path="/alignment" element={<ProtectedRoute><DailyAlignmentPage /></ProtectedRoute>} />
            <Route path="/challenges" element={<ProtectedRoute><ChallengesPage /></ProtectedRoute>} />
            <Route path="/challenges/:challengeId" element={<ProtectedRoute><ChallengeDetailPage /></ProtectedRoute>} />

            {/* Voice Studio Routes */}
            <Route path="/voice-studio" element={<ProtectedRoute><VoiceStudioPage /></ProtectedRoute>} />
            <Route path="/voice-studio/record" element={<ProtectedRoute><VoiceRecordPage /></ProtectedRoute>} />
            <Route path="/voice-studio/create" element={<ProtectedRoute><CreateInstallationPage /></ProtectedRoute>} />
            <Route path="/voice-studio/record-affirmation" element={<ProtectedRoute><RecordAffirmationPage /></ProtectedRoute>} />
            <Route path="/voice-studio/library/:categoryId" element={<ProtectedRoute><AffirmationLibraryPage /></ProtectedRoute>} />
            <Route path="/voice-studio/gift/create" element={<ProtectedRoute><GiftCreatePage /></ProtectedRoute>} />
            <Route path="/voice-studio/clone-setup" element={<ProtectedRoute><VoiceCloneSetupPage /></ProtectedRoute>} />

            {/* Private Suite */}
            <Route path="/private-suite" element={<ProtectedRoute><PrivateSuitePage /></ProtectedRoute>} />
            <Route path="/private-suite/stats" element={<ProtectedRoute><PrivateSuiteStatsPage /></ProtectedRoute>} />
            {/* D3.65 — Personal Audio Journey Builder (lives under Private Suite). */}
            <Route path="/my-journeys" element={<ProtectedRoute><MyJourneysPage /></ProtectedRoute>} />
            <Route path="/my-installations" element={<ProtectedRoute><MyJourneysPage /></ProtectedRoute>} />
            <Route path="/voice-library" element={<ProtectedRoute><VoiceLibraryPage /></ProtectedRoute>} />
            <Route path="/signature-installations" element={<ProtectedRoute><SignatureInstallationsPage /></ProtectedRoute>} />
            <Route path="/signature-installations/world/:worldId" element={<ProtectedRoute><SignatureWorldPage /></ProtectedRoute>} />
            <Route path="/voice-clone" element={<ProtectedRoute><VoiceClonePage /></ProtectedRoute>} />
            <Route path="/voice-gift/:token" element={<VoiceGiftPage />} />
            <Route path="/signature-installations/:categoryId" element={<ProtectedRoute><SignatureCategoryPage /></ProtectedRoute>} />
            <Route path="/voice-library/privacy" element={<ProtectedRoute><PrivacyPromisePage /></ProtectedRoute>} />

            {/* Reclaim Our Abundance - Daily Reality System */}
            {/* PRIMARY: Enter Your Reality - Daily Practice */}
            <Route path="/abundance" element={<ProtectedRoute><EnterYourReality /></ProtectedRoute>} />
            <Route path="/abundance/immerse/:stateId" element={<ProtectedRoute><StateImmersion /></ProtectedRoute>} />
            <Route path="/abundance/:category/bathe" element={<ProtectedRoute><BatheEngine /></ProtectedRoute>} />
            
            {/* Abundance Tools */}
            <Route path="/abundance/winning-moment" element={<ProtectedRoute><WinningMoment /></ProtectedRoute>} />
            <Route path="/abundance/peak-state" element={<ProtectedRoute><PeakStateEngine /></ProtectedRoute>} />
            
            {/* SECONDARY: Edit Your Reality - Vision Board */}
            <Route path="/abundance/edit" element={<ProtectedRoute><EditYourReality /></ProtectedRoute>} />
            
            {/* Living Vision Board V2 - Premium Subconscious Conditioning */}
            <Route path="/abundance/living-vision-board" element={<ProtectedRoute><LivingVisionBoard /></ProtectedRoute>} />
            <Route path="/abundance/my-visions" element={<ProtectedRoute><SavedImmersionsGallery /></ProtectedRoute>} />
            <Route path="/abundance/admin/images" element={<ProtectedRoute><AdminImageManager /></ProtectedRoute>} />
            <Route path="/abundance/admin/videos" element={<ProtectedRoute><AdminVideoManager /></ProtectedRoute>} />
            <Route path="/abundance/legacy" element={<ProtectedRoute><LivingVisionBoard /></ProtectedRoute>} />
            <Route path="/abundance/tile/:category" element={<ProtectedRoute><AbundanceTileExperience /></ProtectedRoute>} />

            {/* Reclaim Our Presence - 21 Day Immersion */}
            <Route path="/reclaim-presence" element={<ProtectedRoute><PresenceLanding /></ProtectedRoute>} />
            <Route path="/reclaim-presence/program" element={<ProtectedRoute><PresenceJourney /></ProtectedRoute>} />
            <Route path="/reclaim-presence/activations" element={<ProtectedRoute><PresenceActivationsPage /></ProtectedRoute>} />
            <Route path="/reclaim-presence/activation/:slug" element={<ProtectedRoute><PresenceActivation /></ProtectedRoute>} />
            <Route path="/reclaim-presence/day/:dayNumber/morning" element={<ProtectedRoute><MorningImmersion /></ProtectedRoute>} />
            <Route path="/reclaim-presence/midday" element={<ProtectedRoute><MiddayInterrupt /></ProtectedRoute>} />
            <Route path="/reclaim-presence/day/:dayNumber/evening" element={<ProtectedRoute><EveningImmersion /></ProtectedRoute>} />
            <Route path="/reclaim-presence/day/:dayNumber/night" element={<ProtectedRoute><NightAudio /></ProtectedRoute>} />
            <Route path="/reclaim-presence/focus" element={<ProtectedRoute><DailyFocusPage /></ProtectedRoute>} />

            {/* Reclaim Our Balance - Emotional Reset Journeys */}
            <Route path="/balance" element={<ProtectedRoute><BalanceHubPage /></ProtectedRoute>} />
            <Route path="/balance/:journeyId" element={<ProtectedRoute><BalanceJourneyPage /></ProtectedRoute>} />
            <Route path="/balance/:journeyId/day/:dayNumber" element={<ProtectedRoute><BalanceDayPage /></ProtectedRoute>} />
            <Route path="/balance/:journeyId/midday-reset" element={<ProtectedRoute><MiddayResetPage /></ProtectedRoute>} />
            <Route path="/balance/:journeyId/day/:dayNumber/release" element={<ProtectedRoute><ReleaseRitualPage /></ProtectedRoute>} />

            {/* Teen Zone */}
            <Route path="/teen" element={<ProtectedRoute><TeenLandingPage /></ProtectedRoute>} />
            <Route path="/teen/zone" element={<TeenZonePage />} />
            <Route path="/teen/challenge" element={<ProtectedRoute><TeenChallengePage /></ProtectedRoute>} />
            <Route path="/teen/affirmations" element={<ProtectedRoute><TeenAffirmationsPage /></ProtectedRoute>} />
            <Route path="/teen/subliminals" element={<ProtectedRoute><TeenSubliminalsPage /></ProtectedRoute>} />
            <Route path="/teen/reset" element={<ProtectedRoute><TeenResetPage /></ProtectedRoute>} />
            <Route path="/teen/share" element={<ProtectedRoute><TeenSharePage /></ProtectedRoute>} />
            <Route path="/teen/history" element={<ProtectedRoute><TeenJourneyHistoryPage /></ProtectedRoute>} />

            {/* Networker Zone */}
            <Route path="/networker" element={<ProtectedRoute><NetworkerLandingPage /></ProtectedRoute>} />
            <Route path="/networker/immersion" element={<ProtectedRoute><NetworkerImmersion /></ProtectedRoute>} />
            <Route path="/networker/onboarding" element={<ProtectedRoute><EdgeOnboarding /></ProtectedRoute>} />
            <Route path="/networker/intro/living-vision"      element={<ProtectedRoute><LivingVisionIntro /></ProtectedRoute>} />
            <Route path="/networker/performance-vision/setup"  element={<ProtectedRoute><PerformanceVisionSetup /></ProtectedRoute>} />
            <Route path="/networker/intro/bank-of-reclaim"    element={<ProtectedRoute><BankOfReclaimIntro /></ProtectedRoute>} />
            <Route path="/networker/intro/state-resets"       element={<ProtectedRoute><StateResetsIntro /></ProtectedRoute>} />
            <Route path="/networker/intro/private-suite"      element={<ProtectedRoute><PrivateSuiteIntro /></ProtectedRoute>} />
            <Route path="/networker/intro/guided-performance" element={<ProtectedRoute><GuidedPerformanceIntro /></ProtectedRoute>} />
            <Route path="/networker/affirmations" element={<ProtectedRoute><NetworkerAffirmationsPage /></ProtectedRoute>} />
            <Route path="/networker/resets" element={<ProtectedRoute><NetworkerResetsPage /></ProtectedRoute>} />
            <Route path="/networker/resets/:resetId" element={<ProtectedRoute><NetworkerResetsPage /></ProtectedRoute>} />
            <Route path="/networker/challenges" element={<ProtectedRoute><NetworkerChallengesPage /></ProtectedRoute>} />
            <Route path="/networker/challenges/:challengeId" element={<ProtectedRoute><NetworkerChallengesPage /></ProtectedRoute>} />
            <Route path="/networker/challenges/:challengeId/day/:dayNumber" element={<ProtectedRoute><NetworkerChallengesPage /></ProtectedRoute>} />
            <Route path="/networker/audio-library" element={<ProtectedRoute><NetworkerAudioLibraryPage /></ProtectedRoute>} />

            {/* Hero's Journey */}
            <Route path="/hero-journey" element={<ProtectedRoute><HeroJourneyPage /></ProtectedRoute>} />
            <Route path="/hero-journey/share" element={<ProtectedRoute><HeroSharePage /></ProtectedRoute>} />
            
            {/* Parent Section */}
            <Route path="/parent" element={<Navigate to="/parent/challenge" replace />} />
            <Route path="/parent/verify" element={<ParentVerificationPage />} />
            <Route path="/parent/challenge" element={<ProtectedRoute><ParentChallengePage /></ProtectedRoute>} />

            {/* Integration Journey (Inner Circle) */}
            <Route path="/integration" element={<ProtectedRoute><IntegrationJourneyPage /></ProtectedRoute>} />
            <Route path="/integration/night/:nightNumber" element={<ProtectedRoute><NightSessionPage /></ProtectedRoute>} />

            {/* Bank of Reclaim */}
            <Route path="/bank-of-reclaim" element={<ProtectedRoute><BankOfReclaimPage /></ProtectedRoute>} />

            {/* Aurum Edge — white-label experience */}
            <Route path="/aurum-edge" element={<ProtectedRoute><AurumEdgeLanding /></ProtectedRoute>} />
            <Route path="/aurum-edge/wealth" element={<ProtectedRoute><AurumWealth /></ProtectedRoute>} />
            <Route path="/aurum-edge/vision" element={<ProtectedRoute><AurumVision /></ProtectedRoute>} />
            <Route path="/aurum-edge/vision/immerse" element={<ProtectedRoute><AurumVisionImmersion /></ProtectedRoute>} />
            <Route path="/aurum-edge/vision/:categoryId" element={<ProtectedRoute><AurumVisionCategory /></ProtectedRoute>} />
            <Route path="/aurum-edge/alignment" element={<ProtectedRoute><AurumAlignment /></ProtectedRoute>} />
            <Route path="/aurum-edge/affirmations" element={<ProtectedRoute><AurumAffirmations /></ProtectedRoute>} />
            <Route path="/aurum-edge/deep/:trackId" element={<ProtectedRoute><AurumDeepWork /></ProtectedRoute>} />
            <Route path="/aurum-edge/private-suite" element={<ProtectedRoute><AurumPrivateSuite /></ProtectedRoute>} />

            {/* Inner Path — sacred / consciousness window */}
            <Route path="/inner-path" element={<ProtectedRoute><InnerPathLanding /></ProtectedRoute>} />
            <Route path="/inner-path/morning-arrival" element={<ProtectedRoute><MorningArrivalRitual /></ProtectedRoute>} />
            <Route path="/inner-path/deep/:trackId" element={<ProtectedRoute><InnerPathDeepWork /></ProtectedRoute>} />
            <Route path="/inner-path/daily/:slot" element={<ProtectedRoute><InnerPathDailyScript /></ProtectedRoute>} />

            {/* Gym — strength / training window (scaffold) */}
            <Route path="/gym" element={<ProtectedRoute><GymLanding /></ProtectedRoute>} />
            <Route path="/stand-easy" element={<ProtectedRoute><StandEasyPage /></ProtectedRoute>} />
            <Route path="/stand-easy/reset/:minutes" element={<ProtectedRoute><StandEasyBreath /></ProtectedRoute>} />
            <Route path="/stand-easy/immersion/:id" element={<ProtectedRoute><StandEasyImmersion /></ProtectedRoute>} />
            <Route path="/stand-easy/:tab" element={<ProtectedRoute><StandEasyPage /></ProtectedRoute>} />
            <Route path="/gym/deep/:trackId" element={<ProtectedRoute><AreaGate area="gym"><GymDeepWork /></AreaGate></ProtectedRoute>} />
            <Route path="/library" element={<ProtectedRoute><MyCollectionPage /></ProtectedRoute>} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/billing/success" element={<BillingSuccessPage />} />
            <Route path="/library/personal" element={<ProtectedRoute><PersonalLibraryPage /></ProtectedRoute>} />
            {/* Public share — no auth required */}
            <Route path="/s/:shareId" element={<SharedItemPage />} />

            {/* Abundance Journey (Reclaim Program Experience System) */}
            <Route path="/journey/abundance" element={<ProtectedRoute><AbundanceJourneyPage /></ProtectedRoute>} />
            <Route path="/journey/abundance/day/:dayNumber" element={<ProtectedRoute><JourneyDayPage /></ProtectedRoute>} />
            <Route path="/journey/abundance/nights" element={<ProtectedRoute><AbundanceNightJourneyPage /></ProtectedRoute>} />
            <Route path="/journey/abundance/night/:nightNumber" element={<ProtectedRoute><AbundanceNightSessionPage /></ProtectedRoute>} />

            {/* Healing Support */}
            <Route path="/healing" element={<ProtectedRoute><HealingSupportHub /></ProtectedRoute>} />
            <Route path="/healing/spinal-ease" element={<ProtectedRoute><SpinalEase /></ProtectedRoute>} />
            <Route path="/healing/spinal-ease/emergency" element={<ProtectedRoute><EmergencyNerveEase /></ProtectedRoute>} />
            <Route path="/healing/spinal-ease/journal" element={<ProtectedRoute><SpinalTrendChart onBack={() => window.history.back()} /></ProtectedRoute>} />
            <Route path="/healing/heart-flow" element={<ProtectedRoute><HeartFlow /></ProtectedRoute>} />
            <Route path="/healing/womb-healing" element={<ProtectedRoute><WombFlow /></ProtectedRoute>} />
            <Route path="/healing/brain-clarity" element={<ProtectedRoute><BrainFlow /></ProtectedRoute>} />
            <Route path="/healing/nervous-system" element={<ProtectedRoute><NervousSystemFlow /></ProtectedRoute>} />

            {/* Instant Alignment */}
            <Route path="/instant-alignment" element={<ProtectedRoute><InstantAlignment /></ProtectedRoute>} />

            {/* Guided Tour */}
            <Route path="/tour" element={<GuidedTour />} />

            {/* Performance */}
            <Route path="/reclaim-performance/golf" element={<ProtectedRoute><GolfPerformancePage /></ProtectedRoute>} />
            <Route path="/golf" element={<ProtectedRoute><GolfInterfaceLanding /></ProtectedRoute>} />
            <Route path="/golf/deep/:trackId" element={<ProtectedRoute><AreaGate area="golf"><GolfDeepWork /></AreaGate></ProtectedRoute>} />
            <Route path="/golf/deeper-work" element={<ProtectedRoute><GolfDeeperWorkList /></ProtectedRoute>} />
            <Route path="/golf/intro/:slug" element={<ProtectedRoute><GolfIntroScreen /></ProtectedRoute>} />
            <Route path="/soulumo/for-coaches" element={<SoulumoForCoaches />} />
            <Route path="/soulumo/coach" element={<ProtectedRoute><CoachPrepPage /></ProtectedRoute>} />
            <Route path="/soulumo/from/:id" element={<CoachGiftLanding />} />
            <Route path="/golf/immersion" element={<ProtectedRoute><GolfImmersion /></ProtectedRoute>} />
            <Route path="/golf/coach" element={<ProtectedRoute><GolfCoachPage /></ProtectedRoute>} />
            <Route path="/atelier/guide" element={<ProtectedRoute><GolfCoachPage coach="atelier" /></ProtectedRoute>} />
            <Route path="/gym/guide" element={<ProtectedRoute><GolfCoachPage coach="gym" /></ProtectedRoute>} />
            <Route path="/foundations/guide" element={<GuideLitePage area="foundations" />} />
            <Route path="/lights/guide" element={<GuideLitePage area="lights" />} />
            <Route path="/soulumo/guide" element={<GuideLitePage area="soulumo" />} />
            <Route path="/networker/guide" element={<ProtectedRoute><GolfCoachPage coach="networker" /></ProtectedRoute>} />
            <Route path="/inner-path/guide" element={<ProtectedRoute><GolfCoachPage coach="inner_path" /></ProtectedRoute>} />
            <Route path="/golf/phrase/:trackId/:phraseIndex" element={<ProtectedRoute><GolfPhraseImmersion /></ProtectedRoute>} />
            {/* Reclaim Rewrite Engine — domain-parametric ritual */}
            <Route path="/golf/reclaim-the-shot" element={<ProtectedRoute><ReclaimRewriteEngine domain="golf" /></ProtectedRoute>} />
            <Route path="/rewrite" element={<ProtectedRoute><RewriteEngineHub /></ProtectedRoute>} />
            <Route path="/clearing/reclaim-the-pattern" element={<ProtectedRoute><ReclaimRewriteEngine domain="clearing" /></ProtectedRoute>} />
            <Route path="/abundance/rewrite-scarcity" element={<ProtectedRoute><ReclaimRewriteEngine domain="abundance" /></ProtectedRoute>} />
            <Route path="/presence/return-to-here" element={<ProtectedRoute><ReclaimRewriteEngine domain="presence" /></ProtectedRoute>} />
            <Route path="/relationships/rewrite-the-armour" element={<ProtectedRoute><ReclaimRewriteEngine domain="relationships" /></ProtectedRoute>} />
            <Route path="/performance/free-under-pressure" element={<ProtectedRoute><ReclaimRewriteEngine domain="performance" /></ProtectedRoute>} />
            <Route path="/rewrite/:domain" element={<ProtectedRoute><ReclaimRewriteEngine /></ProtectedRoute>} />

            {/* Collective Alignment — premium anonymous shared emotional regulation */}
            <Route path="/collective" element={<ProtectedRoute><CollectiveAlignment /></ProtectedRoute>} />
            <Route path="/library/collective" element={<ProtectedRoute><CollectiveLibraryPage /></ProtectedRoute>} />
            <Route path="/performance-vision" element={<ProtectedRoute><PerformanceVisionFlow /></ProtectedRoute>} />

            {/* Gift a Moment — viral sharing feature (affirmation audio) */}
            <Route path="/gift-moment/create" element={<ProtectedRoute><GiftMomentCreatePage /></ProtectedRoute>} />
            <Route path="/gift-moment/:gift_id" element={<GiftMomentViewPage />} />

            {/* Integration Program */}
            <Route path="/integration-program" element={<ProtectedRoute><IntegrationProgramPage /></ProtectedRoute>} />

            {/* Reclaim Absorption Engine — Journeys (UK Driving Theory + future) */}
            <Route path="/journeys" element={<ProtectedRoute><JourneysHub /></ProtectedRoute>} />
            <Route path="/journeys/create" element={<ProtectedRoute><PersonalJourneyCreate /></ProtectedRoute>} />
            <Route path="/personal-journeys/:slug/day/:day" element={<ProtectedRoute><PersonalJourneyDayPage /></ProtectedRoute>} />
            <Route path="/journeys/:slug" element={<ProtectedRoute><JourneyDetailPage /></ProtectedRoute>} />
            <Route path="/journeys/:slug/mock" element={<ProtectedRoute><MockTestPage /></ProtectedRoute>} />
            <Route path="/journeys/:slug/day/:day" element={<ProtectedRoute><AbsorptionDayPage /></ProtectedRoute>} />

            {/* Your Living Dream — Phase 1a (Arrival → Write → Result → Library) */}
            <Route path="/living-dream"             element={<ProtectedRoute><LivingDreamArrival /></ProtectedRoute>} />
            <Route path="/living-dream/write"       element={<ProtectedRoute><LivingDreamWrite /></ProtectedRoute>} />
            <Route path="/living-dream/result/:id"  element={<ProtectedRoute><LivingDreamResult /></ProtectedRoute>} />
            <Route path="/living-dream/board/:id"   element={<ProtectedRoute><LivingDreamBoard /></ProtectedRoute>} />
            <Route path="/living-dream/library"     element={<ProtectedRoute><LivingDreamLibrary /></ProtectedRoute>} />

            {/* Reclaim Atelier — gated by entitlement (UI-side) + 402
                response (server-side) on /api/atelier/dreams/create */}
            <Route path="/atelier"                  element={<ProtectedRoute><AtelierHub /></ProtectedRoute>} />
            <Route path="/atelier/kings-ritual"      element={<KingsRitualHub />} />
            <Route path="/atelier/kings-ritual/:day" element={<KingsRitualDay />} />
            <Route path="/atelier/identity-ritual"   element={<AtelierIdentityRitual />} />
            <Route path="/my-dream"                 element={<ProtectedRoute><MyDream /></ProtectedRoute>} />
            <Route path="/atelier/write"            element={<ProtectedRoute><AtelierWrite /></ProtectedRoute>} />
            <Route path="/atelier/curating/:dreamId" element={<ProtectedRoute><AtelierCurating /></ProtectedRoute>} />
            <Route path="/atelier/:dreamId"         element={<ProtectedRoute><AtelierDashboard /></ProtectedRoute>} />

            {/* Atelier gift reveal — PUBLIC reveal (anyone with the
                code can see the sender + message); redemption itself
                requires auth, bouncing through /login?redirect=... */}
            <Route path="/gift/atelier/:code"       element={<AtelierGiftReveal />} />
            <Route path="/dream/shared/:token"      element={<SharedDream />} />
            <Route path="/partners/golf"            element={<GolfPartnerPage />} />
            <Route path="/partners/gym"             element={<GolfPartnerPage />} />

            {/* Collective Alignment System (Aurum / white-label) — the daily
                team transmission flow. Leader → composer; member → 5-step
                guided journey. Shareable preview is public. */}
            <Route path="/team-alignment"           element={<ProtectedRoute><TeamAlignmentHub /></ProtectedRoute>} />
            <Route path="/team-alignment/:teamId"   element={<ProtectedRoute><TeamAlignmentHub /></ProtectedRoute>} />
            <Route path="/share/alignment/:code"    element={<TeamAlignmentShare />} />
            <Route path="/admin/teams"              element={<ProtectedRoute><AdminTeamsPage /></ProtectedRoute>} />

            {/* 3-tier Affiliate MLM dashboard */}
            <Route path="/affiliate"                element={<ProtectedRoute><AffiliateMLMDashboard /></ProtectedRoute>} />
            <Route path="/partner-pack"             element={<ProtectedRoute><PartnerPackPage /></ProtectedRoute>} />
            <Route path="/admin/mlm"                element={<ProtectedRoute><AdminMLMPage /></ProtectedRoute>} />

            {/* Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute><AdminOverview /></ProtectedRoute>} />
            <Route path="/admin/programs" element={<ProtectedRoute><AdminPrograms /></ProtectedRoute>} />
            <Route path="/admin/programs/:programId" element={<ProtectedRoute><AdminProgramDetail /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/guides" element={<ProtectedRoute><AdminGuides /></ProtectedRoute>} />
            <Route path="/admin/costs" element={<ProtectedRoute><AdminCosts /></ProtectedRoute>} />
            <Route path="/admin/library-growth" element={<ProtectedRoute><AdminLibraryGrowth /></ProtectedRoute>} />
            <Route path="/admin/affiliate-tax" element={<ProtectedRoute><AdminAffiliateTax /></ProtectedRoute>} />
            <Route path="/soulumo/affiliate-details" element={<ProtectedRoute><AffiliateDetailsPage /></ProtectedRoute>} />
            <Route path="/admin/commissions" element={<ProtectedRoute><AdminCommissions /></ProtectedRoute>} />
            <Route path="/soulumo/membership" element={<ProtectedRoute><MembershipPage /></ProtectedRoute>} />
            <Route path="/admin/talking-clips" element={<ProtectedRoute><AdminTalkingClips /></ProtectedRoute>} />
            <Route path="/admin/affiliates" element={<ProtectedRoute><AdminAffiliates /></ProtectedRoute>} />
            <Route path="/admin/images" element={<ProtectedRoute><AdminImages /></ProtectedRoute>} />
            <Route path="/admin/challenges" element={<ProtectedRoute><AdminChallenges /></ProtectedRoute>} />
            <Route path="/admin/integration-program" element={<ProtectedRoute><AdminIntegrationProgram /></ProtectedRoute>} />
            <Route path="/admin/audio" element={<ProtectedRoute><AdminAudioStatus /></ProtectedRoute>} />
            <Route path="/admin/foundations-raw" element={<ProtectedRoute><AdminFoundationsRaw /></ProtectedRoute>} />
            <Route path="/foundations/redeem" element={<FoundationsRedeem />} />
            <Route path="/circles/redeem/:token" element={<CirclesRedeemPage />} />
            <Route path="/your-moment" element={<Navigate to="/foundations" replace />} />
            <Route path="/admin/voice-coverage" element={<ProtectedRoute><AdminVoiceCoverage /></ProtectedRoute>} />
            <Route path="/admin/foundations/upload" element={<ProtectedRoute><AdminFoundationsUpload /></ProtectedRoute>} />
            <Route path="/admin/circles" element={<ProtectedRoute><AdminCirclesOfCare /></ProtectedRoute>} />
            <Route path="/admin/voice-bank-recorder" element={<ProtectedRoute><AdminVoiceBankRecorder /></ProtectedRoute>} />
            <Route path="/admin/audio/resync" element={<ProtectedRoute><AdminAudioResync /></ProtectedRoute>} />
            <Route path="/admin/gift-priming" element={<ProtectedRoute><AdminGiftPriming /></ProtectedRoute>} />
            <Route path="/biohacking/sleep" element={<ProtectedRoute><BiohackingSleep /></ProtectedRoute>} />
            <Route path="/foundations/sleep" element={<ProtectedRoute><FoundationsSleepPage /></ProtectedRoute>} />
            <Route path="/daily-essentials" element={<ProtectedRoute><DailyEssentialsPage /></ProtectedRoute>} />
            {/* PUBLIC share landing — no auth, viral entry point. */}
            <Route path="/share/morning-activation" element={<MorningActivationShare />} />
            {/* PUBLIC bespoke / personal audio share — stable slug-keyed URLs.
                e.g. /share/audio/sam-night-3hr */}
            <Route path="/share/audio/:slug" element={<PublicShareAudio />} />
            {/* ADMIN — mint new public share URLs without code deploys. */}
            <Route path="/admin/share-links" element={<ProtectedRoute><AdminShareLinks /></ProtectedRoute>} />
            <Route path="/admin/family-keepsakes" element={<ProtectedRoute><AdminFamilyKeepsakes /></ProtectedRoute>} />
            <Route path="/admin/golf/scripts" element={<ProtectedRoute><AdminGolfScripts /></ProtectedRoute>} />
            <Route path="/admin/golf/signatures" element={<ProtectedRoute><AdminGolfSignatures /></ProtectedRoute>} />
            <Route path="/admin/concierge" element={<ProtectedRoute><AdminConcierge /></ProtectedRoute>} />
            <Route path="/admin/bulk-invite" element={<ProtectedRoute><AdminBulkInvite /></ProtectedRoute>} />
            <Route path="/admin/beta-invites" element={<ProtectedRoute><AdminBetaInvites /></ProtectedRoute>} />
            <Route path="/admin/influencers" element={<ProtectedRoute><AdminInfluencers /></ProtectedRoute>} />
            <Route path="/admin/golf-partners" element={<ProtectedRoute><AdminGolfPartners /></ProtectedRoute>} />
            <Route path="/with/:slug" element={<InfluencerEntry />} />
            <Route path="/with-stats/:token" element={<InfluencerStats />} />
            <Route path="/soulumo/partner" element={<ProtectedRoute><PartnerAreaPage /></ProtectedRoute>} />
            <Route path="/admin/stuck-resets" element={<ProtectedRoute><AdminStuckResets /></ProtectedRoute>} />
            <Route path="/admin/grant-access" element={<ProtectedRoute><AdminGrantAccess /></ProtectedRoute>} />
            <Route path="/admin/merge-accounts" element={<ProtectedRoute><AdminMergeAccounts /></ProtectedRoute>} />
            <Route path="/admin/library-restore" element={<ProtectedRoute><AdminLibraryRestore /></ProtectedRoute>} />
            <Route path="/soulumo/demo" element={<SoulumoDemoPage />} />
            <Route path="/soulumo/launch" element={<SoulumoLaunchPage />} />
            <Route path="/soulumo/launch/heroes" element={<SoulumoHeroOptions />} />
            <Route path="/launch" element={<SoulumoLaunchPage />} />
            <Route path="/together/new" element={<ProtectedRoute><TogetherSetupPage /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute><AdminPayments /></ProtectedRoute>} />
            <Route path="/admin/voice-clone-interest" element={<ProtectedRoute><AdminVoiceCloneInterest /></ProtectedRoute>} />
            <Route path="/admin/founding-light-reservations" element={<ProtectedRoute><AdminFoundingLightReservations /></ProtectedRoute>} />
            <Route path="/admin/beta-feedback" element={<ProtectedRoute><AdminBetaIntelligence /></ProtectedRoute>} />
            <Route path="/admin/beta-intelligence" element={<ProtectedRoute><AdminBetaIntelligence /></ProtectedRoute>} />
            <Route path="/admin/journey-diagnostics" element={<ProtectedRoute><AdminJourneyDiagnostics /></ProtectedRoute>} />
            <Route path="/admin/exports" element={<ProtectedRoute><AdminExports /></ProtectedRoute>} />
            <Route path="/login/magic" element={<MagicLoginPage />} />
            <Route path="/login/magic/:code" element={<MagicLoginPage />} />
            {/* Referral landing — captures the invite code and routes
                the visitor to sign-up / sign-in with attribution. */}
            <Route path="/join/:code" element={<JoinReferralPage />} />
            <Route path="/admin/journey-images" element={<ProtectedRoute><AdminJourneyImages /></ProtectedRoute>} />
            <Route path="/admin/networker-vision-images" element={<ProtectedRoute><AdminNetworkerVisionImages /></ProtectedRoute>} />
            <Route path="/admin/excelior-training" element={<ProtectedRoute><AdminExceliorTraining /></ProtectedRoute>} />

            {/* Pre-Performance Priming — mental warm-up for key moments */}
            <Route path="/networker/prime" element={<ProtectedRoute><PrePerformancePriming /></ProtectedRoute>} />
            <Route path="/networker/prime/curate" element={<ProtectedRoute><PrePerformanceCurate /></ProtectedRoute>} />
            <Route path="/networker/prime/session/:sessionId" element={<ProtectedRoute><PrePerformanceSession /></ProtectedRoute>} />

            {/* Practices — Resets · State Tuning · Tapping */}
            <Route path="/practices" element={<ProtectedRoute><PracticesHome /></ProtectedRoute>} />
            <Route path="/practices/resets" element={<ProtectedRoute><ResetsIntro /></ProtectedRoute>} />
            <Route path="/football" element={<ProtectedRoute><FootballHome /></ProtectedRoute>} />
            {/* Absorption Engine — PDF-in / study-installation-out.
                Un-parked Feb 18 2026 for Anastasia pilot: the engine
                (chunked upload, chapter extraction, flashcards, 7-stage
                install) was already done; it just needed a live route. */}
            <Route path="/absorption" element={<ProtectedRoute><AbsorptionEngine /></ProtectedRoute>} />
            <Route path="/practices/state-tuning" element={<ProtectedRoute><StateTuningIntro /></ProtectedRoute>} />
            <Route path="/practices/state-tuning/select" element={<ProtectedRoute><StateTuningSelect /></ProtectedRoute>} />
            <Route path="/practices/state-tuning/play/:stateId" element={<ProtectedRoute><StateTuningPlay /></ProtectedRoute>} />
            <Route path="/practices/tapping" element={<ProtectedRoute><TappingIntro /></ProtectedRoute>} />
            <Route path="/practices/tapping/select" element={<ProtectedRoute><TappingSelect /></ProtectedRoute>} />
            <Route path="/practices/tapping/play/:sessionId" element={<ProtectedRoute><TappingPlay /></ProtectedRoute>} />
          </Routes>
              </Suspense>
            </ErrorBoundary>

          {/* Global BottomNav — appears on every route except the
              immersive full-screen experiences listed inside the
              component (journeys, resets, onboarding etc.). Page-level
              <BottomNav/> imports remain for pages that already render
              it; duplicates are hidden via CSS.
              Mar 5 2026 · All Reclaim floating chrome (back button,
              bottom nav, Reset/Presence FABs, Assistant) is suppressed
              on Soulumo & OML hosts so those landings feel like their
              own product, not a section of Reclaim.
              Feb 22 2026 · Also suppressed on /foundations/* — the
              premium gift experience should feel like its own space,
              never a section of Reclaim. */}
          <NarrowChromeGate narrowBrand={narrowBrand}>
            <GlobalBackButton />
            <GlobalBottomNav />
          </NarrowChromeGate>
          <StandEasySkin />
          <SoulumoTabBar />
          <ExceliorRouteTracker />

          <NarrowChromeGate narrowBrand={narrowBrand}>
            {/* Floating Reset Button - available on all pages */}
            <FloatingResetButton />

            {/* Floating Presence Button - "Enter Presence" for midday reset */}
            <FloatingPresenceButton />

            {/* Reclaim Assistant - floating guide for new users */}
            <ReclaimAssistantWrapper />
          </NarrowChromeGate>
          <SoulumoGuideButton />
          <NarrowChromeGate narrowBrand={narrowBrand}>
            <PreviewModeIndicator />
          </NarrowChromeGate>
          
          {/* Alignment Check - appears once per day */}
          <AlignmentCheckWrapper 
            show={showAlignmentSignal} 
            onClose={handleAlignmentClose}
          />

          {/* Premium Suite · Signature Moment #2 — bottom-docked sacred
              player. REMOVED from global render Jun 10 2026 — Michael
              reported the dock was surfacing on every page as
              unwanted chrome. The component file is kept intact in
              case we want to bring it back wired to an explicit user
              action rather than auto-populating from /library/items.
              Original mount:  <BottomDockedPlayer /> */}
        </BrowserRouter>
        
        {/* Sonner toast portal — mounted at root so the live
            "today's transmission has dropped" nudge (and any other
            toast() callers across the app) actually surface. */}
        <Toaster position="top-center" richColors closeButton />
        <ViewAsBanner />
        <CreditsSheet />

        {/* Daily Ritual Overlay - rendered last to be on top of everything */}
        {showDailyRitual && (
          <DailyRitualWrapper 
            onComplete={handleRitualComplete}
            onSkip={handleRitualSkip}
          />
        )}
          </GlobalTeenTheme>
      </AuthProvider>
    </ReferralProvider>
    </ErrorBoundary>
  );
}

// Track route so Excelior knows where the user came from before opening chat.
function ExceliorRouteTracker() {
  const location = useLocation();
  useEffect(() => {
    const p = location.pathname;
    if (!p) return;
    // Don't record Excelior's own pages as the "previous" route.
    if (p.startsWith("/excelior") || p.startsWith("/ask-michael")) return;
    try { localStorage.setItem("excelior_previous_route", p); } catch (_) {}
  }, [location.pathname]);
  return null;
}

// Wrapper component for AlignmentCheck to enable navigation
function AlignmentCheckWrapper({ show, onClose }) {
  const location = useLocation();
  
  // Routes that bypass alignment check (immersive or premium experiences with their own flow)
  const bypassRoutes = [
    '/abundance',      // Living Vision Board has own pre-entry
    '/private-suite',  // Premium ritual experience
    '/bank-of-reclaim', // Visualization exercise
    '/journey',        // 21-night journey sessions
    '/integration',    // Integration night sessions
    '/teen/zone',      // Teen zone has own flow
    '/reclaim-presence', // Presence Journey has own Entry Transition
    '/parent',          // Parent Zone
    '/healing',         // Healing Support has own immersive flow
    '/instant-alignment', // Instant Alignment is fully immersive
    '/tour',              // Guided Tour is fully immersive
    '/reclaim-performance', // Performance experiences are fully immersive
    '/admin',               // Admin pages bypass alignment
    '/integration-program',  // Program pages bypass alignment
    '/reclaim-presence/focus', // Focus immersion is fully immersive
    '/excelior',            // Excelior chat has its own focused flow
    '/ask-michael',         // Same as /excelior
    '/journeys',            // Absorption Engine — calm, immersive flow
    '/heart-ru',            // Public grandma page — no chrome at all
    '/spine/',              // Public mum spine page — no chrome
    '/heart/',              // Public dad heart page — no chrome
    '/gift/',               // Public gift page
    '/listen/',             // Public listen page
    '/share/audio/',        // Public share / gift page
  ];
  
  // Don't show alignment check on these routes
  if (bypassRoutes.some(route => location.pathname.startsWith(route))) {
    return null;
  }
  
  const handleComplete = (alignmentData) => {
    console.log("Alignment completed:", alignmentData);
    // The AlignmentCheck component handles navigation internally
  };
  
  return (
    <AlignmentCheck 
      isOpen={show} 
      onClose={onClose}
      onComplete={handleComplete}
    />
  );
}

// Floating guide on Soulumo Library pages: opens the creation guide chat. Not on Create
// (/my-journeys): there it sat over the Begin button on phones; Create has its own Adelaida link.
const SOULUMO_GUIDE_PATHS = ["/my-installations", "/my-library", "/library"];
function SoulumoGuideButton() {
  const { pathname } = useLocation();
  if (!SOULUMO_GUIDE_PATHS.includes(pathname)) return null;
  return <><div aria-hidden style={{ height: 150 }} /><GuideBubble to="/soulumo/guide?help=1" label="Soulumo" testId="soulumo-guide-button" compact /></>;
}

// Wrapper component for ReclaimAssistant to enable navigation
function ReclaimAssistantWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Hide assistant on teen zone, abundance, healing, instant alignment, integration night sessions, journey night sessions (they have their own focused flows)
  if (
    location.pathname.startsWith('/teen/zone') ||
    location.pathname.startsWith('/abundance') ||
    location.pathname.startsWith('/healing') ||
    location.pathname.startsWith('/instant-alignment') ||
    location.pathname.startsWith('/tour') ||
    location.pathname.startsWith('/integration/night') ||
    location.pathname.startsWith('/journey/abundance/night') ||
    location.pathname.startsWith('/journeys') ||
    location.pathname.startsWith('/gift-moment') ||
    location.pathname.startsWith('/share/audio') ||
    location.pathname.startsWith('/gift/') ||
    location.pathname.startsWith('/heart-ru/') ||
    location.pathname.startsWith('/spine/') ||
    location.pathname.startsWith('/heart/') ||
    location.pathname.startsWith('/listen') ||
    location.pathname.startsWith('/inner-path/morning-arrival') ||
    location.pathname.startsWith('/inner-path/deep/') ||
    location.pathname.startsWith('/inner-path/daily/') ||
    location.pathname.startsWith('/team-alignment') ||
    location.pathname.startsWith('/share/alignment') ||
    location.pathname.startsWith('/atelier') ||
    location.pathname.startsWith('/affiliate') ||
    location.pathname.startsWith('/partners') ||
    location.pathname.startsWith('/dream/shared')
  ) {
    return null;
  }

  return <ReclaimAssistant onNavigate={navigate} />;
}

// Wrapper for DailyRitualPage — bypasses on focused/immersive routes.
// Lives OUTSIDE BrowserRouter so it can't use useLocation; reads window.location instead.
function DailyRitualWrapper({ onComplete, onSkip }) {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const bypassRoutes = [
    '/excelior',
    '/ask-michael',
    '/admin',
    '/integration/night',
    '/journey/abundance/night',
    '/journeys',
    '/instant-alignment',
    '/healing',
    '/abundance',
    '/integration-program',
    '/private-suite',
    '/tour',
    '/reclaim-performance',
    '/reclaim-presence/focus',
  ];
  if (bypassRoutes.some((r) => path.startsWith(r))) {
    return null;
  }
  return <DailyRitualPage onComplete={onComplete} onSkip={onSkip} />;
}

export default App;
